import * as React from 'react';

import {
  ASKNEEDL_FULLSCREEN_ROUTE,
  SEARCH_ROUTES,
  SOURCE_NAV_MANAGER_ROUTES,
} from '@/nav-routes';

import type {
  FeaturesConfigKey,
  FeaturesConfigType,
} from './query/useFeatureConfig';
import { useConfigUI } from './query/useFeatureConfig';

const VALID_TABS = {
  RSS_NEWS: '/rss_news',
  EXCHANGES: '/exchanges',
  CHATS: '/chats',
  EMAILS: '/emails',
  SOCIAL_MEDIA: '/socialmedia',
  DRIVES: '/drives',
  NOTES: '/notes',
};

export const useGetLandingPage = (): UseGetLandingPageType => {
  const { data, isLoading } = useConfigUI();

  const getFallbackRoute = (featureConfig: FeaturesConfigType): string => {
    const defaultLandingPage = import.meta.env.VITE_DEFAULT_LANDING_PAGE;

    if (defaultLandingPage) {
      if (Object.values(VALID_TABS).includes(defaultLandingPage)) {
        return defaultLandingPage;
      } else {
        throw new Error(
          `Invalid landing page route: ${defaultLandingPage}. Valid routes are: ${Object.values(
            VALID_TABS
          ).join(', ')}`
        );
      }
    }

    if (!featureConfig) {
      return '';
    }

    switch (true) {
      case featureConfig.websites:
        return VALID_TABS.RSS_NEWS;
      case featureConfig.capital_markets:
        return VALID_TABS.EXCHANGES;
      case featureConfig.apps_ms_teams ||
        featureConfig.apps_slack ||
        featureConfig.apps_telegram:
        return VALID_TABS.CHATS;
      case featureConfig.apps_outlook || featureConfig.apps_gmail:
        return VALID_TABS.EMAILS;
      case featureConfig.apps_twitter:
        return VALID_TABS.SOCIAL_MEDIA;
      case featureConfig.apps_sharepoint ||
        featureConfig.apps_box ||
        featureConfig.apps_onedrive ||
        featureConfig.apps_dropbox ||
        featureConfig.apps_google_drive ||
        featureConfig.needldrive:
        return VALID_TABS.DRIVES;
      case featureConfig.apps_onenote || featureConfig.apps_evernote:
        return VALID_TABS.NOTES;
      default:
        return '';
    }
  };

  const FALLBACK_DEFAULT_ROUTE =
    '/feeds/home' + getFallbackRoute(data as FeaturesConfigType);

  // maintain sequence as per left nav
  const FEATURES_NAV_ROUTES = React.useMemo<FeaturesNavRouteType[]>(
    () => [
      { feature: 'my_data', route: FALLBACK_DEFAULT_ROUTE },
      { feature: 'search', route: SEARCH_ROUTES.FULLSCREEN },
      { feature: 'ask_needl_full_screen', route: ASKNEEDL_FULLSCREEN_ROUTE },
      { feature: 'bookmarks', route: '/needlbox/bookmarks/all' },
      { feature: 'tags', route: '/needlbox/tags/all' },
      { feature: 'comments', route: '/needlbox/comments/all' },
      {
        feature: 'assistants',
        route: SOURCE_NAV_MANAGER_ROUTES.ASSISTANTS_ROUTE,
      },
      {
        feature: 'report_generation',
        route: SOURCE_NAV_MANAGER_ROUTES.REPORT_GENERATION_ROUTE,
      },
      {
        feature: 'needl_report',
        route: SOURCE_NAV_MANAGER_ROUTES.REPORT_ROUTE,
      },
    ],
    [FALLBACK_DEFAULT_ROUTE]
  );

  const landingPageRoute = React.useMemo(() => {
    if (isLoading || !data) {
      return FALLBACK_DEFAULT_ROUTE;
    }

    for (const { feature, route } of FEATURES_NAV_ROUTES) {
      if (data[feature]) {
        return route;
      }
    }

    return FALLBACK_DEFAULT_ROUTE;
  }, [FALLBACK_DEFAULT_ROUTE, FEATURES_NAV_ROUTES, data, isLoading]);

  return {
    isLoading,
    landingPageRoute,
  };
};

type FeaturesNavRouteType = {
  feature: FeaturesConfigKey;
  route: string;
};

type UseGetLandingPageType = {
  isLoading: boolean;
  landingPageRoute: string;
};
